import { useQuery, useMutation, useQueryClient } from 'react-query';
import { projectApi } from '../services/api';
import { useToast } from './useToast';
import type { ProjectCreate, ProjectUpdate } from '../types';

export function useProjects(statusFilter?: 'active' | 'completed' | 'suspended' | 'all') {
  return useQuery(['projects', statusFilter], () => projectApi.getAll(statusFilter));
}

export function useProject(projectId: string) {
  return useQuery(['projects', projectId], () => projectApi.getById(projectId), {
    enabled: !!projectId,
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  
  return useMutation((data: ProjectCreate) => projectApi.create(data), {
    onSuccess: () => {
      queryClient.invalidateQueries(['projects']);
      showSuccess('Proyecto creado exitosamente');
    },
    onError: (error) => {
      showError(error, 'Error al crear el proyecto');
    },
  });
}

export function useUpdateProject() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  
  return useMutation(
    ({ projectId, data }: { projectId: string; data: ProjectUpdate }) =>
      projectApi.update(projectId, data),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries(['projects']);
        queryClient.invalidateQueries(['projects', variables.projectId]);
        showSuccess('Proyecto actualizado exitosamente');
      },
      onError: (error) => {
        showError(error, 'Error al actualizar el proyecto');
      },
    }
  );
}

export function useChangeProjectStatus() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  
  return useMutation(
    ({ projectId, status }: { projectId: string; status: 'active' | 'completed' | 'suspended' }) =>
      projectApi.changeStatus(projectId, status),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries(['projects']);
        queryClient.invalidateQueries(['projects', variables.projectId]);
        
        const statusLabels = {
          active: 'Activo',
          completed: 'Finalizado',
          suspended: 'Detenido'
        };
        showSuccess(`Proyecto marcado como ${statusLabels[variables.status]}`);
      },
      onError: (error) => {
        showError(error, 'Error al cambiar el estado del proyecto');
      },
    }
  );
}

export function useDeleteProject() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  
  return useMutation((projectId: string) => projectApi.delete(projectId), {
    onSuccess: () => {
      queryClient.invalidateQueries(['projects']);
      showSuccess('Proyecto eliminado exitosamente');
    },
    onError: (error) => {
      showError(error, 'Error al eliminar el proyecto');
    },
  });
}
